import React, { useState } from "react";
import styled, { keyframes } from "styled-components";

const blink = keyframes`
  0%, 49% { opacity: 1; }
  50%, 100% { opacity: 0; }
`;

const Wrap = styled.div`
  background: var(--nd-surface);
  border: 1px solid var(--nd-border-visible);
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 32px;
  font-family: 'Space Mono', monospace;
`;

const TitleBar = styled.div`
  background: var(--nd-surface-raised);
  border-bottom: 1px solid var(--nd-border);
  padding: 10px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TitleDots = styled.div`
  display: flex;
  gap: 6px;
`;

const TitleDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ color }) => color || "var(--nd-border-visible)"};
`;

const TitlePath = styled.span`
  font-size: 11px;
  letter-spacing: 0.06em;
  color: var(--nd-text-disabled);
  flex: 1;
  text-align: center;
`;

const Body = styled.div`
  padding: 18px 20px;
  background: var(--nd-black);
  color: var(--nd-text-primary);
  font-size: 12px;
  line-height: 1.7;
  overflow-x: auto;
  white-space: pre;
`;

const Line = styled.div`
  white-space: pre;
`;

const Prompt = styled.span`
  color: var(--nd-success);
`;

const Comment = styled.span`
  color: var(--nd-text-disabled);
`;

const Output = styled.span`
  color: var(--nd-text-secondary);
`;

const Caret = styled.span`
  display: inline-block;
  width: 8px;
  height: 1em;
  background: var(--nd-text-display);
  vertical-align: -2px;
  animation: ${blink} 1.1s steps(1) infinite;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  padding: 12px 16px;
  background: var(--nd-surface-raised);
  border-top: 1px solid var(--nd-border);
  flex-wrap: wrap;
  align-items: center;
`;

const ActionBtn = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid var(--nd-border-visible);
  background: transparent;
  color: var(--nd-text-primary);
  cursor: pointer;
  outline: none;
  text-decoration: none !important;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  &:hover {
    border-color: var(--nd-text-display);
    color: var(--nd-text-display);
  }
`;

const PrimaryActionBtn = styled(ActionBtn)`
  background: var(--nd-text-display);
  color: var(--nd-black);
  border-color: var(--nd-text-display);
  &:hover {
    opacity: 0.85;
    color: var(--nd-black);
  }
`;

const Hint = styled.span`
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  margin-left: auto;
`;

const INSTALL_SNIPPET =
  'curl -sSL https://www.dioscuro.com/dioscuro-time -o /usr/local/bin/dioscuro-time \\\n  && chmod +x /usr/local/bin/dioscuro-time';

const TimeTrackerCli = () => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(INSTALL_SNIPPET);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[cli] clipboard failed:", err);
    }
  };

  return (
    <Wrap aria-label="dioscuro-time CLI preview">
      <TitleBar>
        <TitleDots>
          <TitleDot color="#ff5f57" />
          <TitleDot color="#febc2e" />
          <TitleDot color="#28c840" />
        </TitleDots>
        <TitlePath>~ dioscuro-time</TitlePath>
      </TitleBar>
      <Body>
        <Line><Prompt>$</Prompt> <Comment># install</Comment></Line>
        <Line><Prompt>$</Prompt> curl -sSL https://www.dioscuro.com/dioscuro-time -o /usr/local/bin/dioscuro-time</Line>
        <Line><Prompt>$</Prompt> chmod +x /usr/local/bin/dioscuro-time</Line>
        <Line>{" "}</Line>
        <Line><Prompt>$</Prompt> <Comment># sign in (Google device-code flow)</Comment></Line>
        <Line><Prompt>$</Prompt> dioscuro-time login</Line>
        <Line><Output>1. Open: https://www.google.com/device</Output></Line>
        <Line><Output>2. Enter code: ABCD-EFGH</Output></Line>
        <Line><Output>✓ Signed in as fp@dioscuro.com</Output></Line>
        <Line>{" "}</Line>
        <Line><Prompt>$</Prompt> <Comment># log hours (writes to Firestore)</Comment></Line>
        <Line><Prompt>$</Prompt> dioscuro-time log "Acme — K8s migration" 3.5 "Helm chart cleanup"</Line>
        <Line><Output>✓ 2026-05-16 · Acme — K8s migration · 3.5h · Helm chart cleanup · id=xY7…</Output></Line>
        <Line>{" "}</Line>
        <Line><Prompt>$</Prompt> dioscuro-time submit xY7</Line>
        <Line><Output>✓ submitted xY7</Output></Line>
        <Line>{" "}</Line>
        <Line><Prompt>$</Prompt> dioscuro-time list --limit 3</Line>
        <Line><Output>{"DATE         STATUS     PROJECT                        HOURS   ID"}</Output></Line>
        <Line><Output>{"2026-05-16   submitted  Acme — K8s migration           3.5     xY7…"}</Output></Line>
        <Line><Output>{"2026-05-15   draft      Foundry — CI pipeline          4.0     aB2…"}</Output></Line>
        <Line>{" "}</Line>
        <Line><Prompt>$</Prompt> <Caret /></Line>
      </Body>
      <Actions>
        <PrimaryActionBtn as="a" href="/dioscuro-time" download="dioscuro-time">
          ↓ Download
        </PrimaryActionBtn>
        <ActionBtn type="button" onClick={handleCopy}>
          {copied ? "✓ Copied" : "Copy install"}
        </ActionBtn>
        <Hint>v0.2.0 · real Firebase sync</Hint>
      </Actions>
    </Wrap>
  );
};

export default TimeTrackerCli;
